'use client';

import {
  FloatingFocusManager,
  type FloatingFocusManagerProps,
  useMergeRefs,
} from '@floating-ui/react';
import { forwardRef, useContext } from 'react';

import { css, cx, splitResponsiveVariant } from '@/styled-system/css';

import { Slot } from '../slot';
import { ModalFloatingContext } from './context/modal-floating.context';
import { ModalRootContext } from './context/modal-root.context';
import { modalContentRecipe } from './styles/modal-content.styles';
import type { ModalContentPropsInterface } from './types/modal-content.types';

/**
 * @since 10.0.0
 */
export const ModalContent = forwardRef<HTMLDivElement, ModalContentPropsInterface>(
  (props: ModalContentPropsInterface, forwardedRef) => {
    const { modal, scrollDocument, size } = useContext(ModalRootContext);
    const floating = useContext(ModalFloatingContext);
    const {
      asChild = false,
      children,
      className,
      css: cssProp,
      focusManagerProps: userFocusManagerProps = {},
      ...rest
    } = props;

    const responsiveSize = splitResponsiveVariant('size', size);
    const styles = css(modalContentRecipe.raw({ scrollDocument, ...responsiveSize }), cssProp);

    /*
     * Omitting `floating` from the order keeps the dialog container out of the
     * tab order. Floating UI then falls back to `tabindex="0"` on the container
     * only when the modal has no tabbable content of its own, and initial focus
     * lands on the first tabbable element (usually `Modal.CloseButton`).
     */
    const focusManagerProps: Partial<FloatingFocusManagerProps> = {
      order: ['content'],
      ...userFocusManagerProps,
    };

    const mergedRefs = useMergeRefs([modal.refs.setFloating, forwardedRef]);

    const Component = asChild ? Slot : 'div';

    return (
      <FloatingFocusManager context={floating.context} {...focusManagerProps}>
        <Component
          ref={mergedRefs}
          aria-modal="true"
          aria-labelledby={modal.labelId}
          aria-describedby={modal.descriptionId}
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="Modal.Content"
          className={cx('c11n', 'c11n_modal__content', styles, className)}
          {...floating.getFloatingProps(rest)}
          // Override id when using multi modal trigger id
          {...(modal.multiModalTriggerId ? { id: modal.multiModalTriggerId } : {})}
        >
          {children}
        </Component>
      </FloatingFocusManager>
    );
  },
);

ModalContent.displayName = 'Modal.Content';
